// ============================================
// Component: AdminReturnsPage
// Purpose: Manage return requests (RMA) - view, approve, reject
// UX Notes:
// - Clear status badges with colors
// - Quick actions for approve/reject
// - Detailed modal view
// - RTL Hebrew support
// ============================================

import React, { useState, useEffect, useCallback } from 'react';
import { 
  MagnifyingGlassIcon,
  FunnelIcon,
  CheckCircleIcon,
  XCircleIcon,
  EyeIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
  ClockIcon,
  TruckIcon,
  CurrencyDollarIcon,
  XMarkIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';
import toast from 'react-hot-toast';

const AdminReturnsPage = () => {
  // State
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [selectedReturn, setSelectedReturn] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    status: '',
    return_type: '',
    priority: '',
    created_by_agent: '',
    search: '',
    date_from: '',
    date_to: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  
  // Pagination
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  // Form data for approve/reject
  const [approveData, setApproveData] = useState({
    credit_amount: 0,
    return_to_inventory: true,
    internal_notes: ''
  });
  const [rejectData, setRejectData] = useState({
    rejection_reason: '',
    internal_notes: ''
  });

  // Fetch returns
  const fetchReturns = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('page', pagination.page);
      params.append('limit', pagination.limit);
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await axios.get(`/api/admin/returns?${params.toString()}`);
      setReturns(response.data.returns || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination?.total || 0,
        pages: response.data.pagination?.pages || 0
      }));
    } catch (error) {
      toast.error('שגיאה בטעינת החזרות');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, filters]);

  // Fetch stats
  const fetchStats = async () => {
    try {
      const response = await axios.get('/api/admin/returns/stats');
      setStats(response.data.stats);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  useEffect(() => {
    fetchReturns();
    fetchStats();
  }, [fetchReturns]);

  // Status helpers
  const getStatusBadge = (status) => {
    const statusMap = {
      'requested': { label: 'נשלח', color: 'bg-gray-100 text-gray-800', icon: '📝' },
      'pending_approval': { label: 'ממתין לאישור', color: 'bg-yellow-100 text-yellow-800', icon: '⏳' },
      'approved': { label: 'אושר', color: 'bg-green-100 text-green-800', icon: '✅' },
      'rejected': { label: 'נדחה', color: 'bg-red-100 text-red-800', icon: '❌' },
      'label_sent': { label: 'תווית נשלחה', color: 'bg-blue-100 text-blue-800', icon: '📧' },
      'in_transit': { label: 'בדרך', color: 'bg-indigo-100 text-indigo-800', icon: '🚚' },
      'received': { label: 'התקבל', color: 'bg-purple-100 text-purple-800', icon: '📦' },
      'inspecting': { label: 'בבדיקה', color: 'bg-orange-100 text-orange-800', icon: '🔍' },
      'completed': { label: 'הושלם', color: 'bg-green-100 text-green-800', icon: '✓' },
      'cancelled': { label: 'בוטל', color: 'bg-red-100 text-red-800', icon: '🚫' }
    };
    const info = statusMap[status] || { label: status, color: 'bg-gray-100 text-gray-800', icon: '•' };
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${info.color}`}>
        <span>{info.icon}</span>
        {info.label}
      </span>
    );
  };

  const getReturnTypeBadge = (type) => {
    const typeMap = {
      'defective': { label: 'פגום', color: 'bg-red-50 text-red-700' },
      'wrong_item': { label: 'מוצר שגוי', color: 'bg-orange-50 text-orange-700' },
      'not_as_described': { label: 'לא כמתואר', color: 'bg-yellow-50 text-yellow-700' },
      'damaged_in_shipping': { label: 'נזק במשלוח', color: 'bg-purple-50 text-purple-700' },
      'changed_mind': { label: 'שינוי דעה', color: 'bg-blue-50 text-blue-700' },
      'warranty_claim': { label: 'אחריות', color: 'bg-indigo-50 text-indigo-700' },
      'other': { label: 'אחר', color: 'bg-gray-50 text-gray-700' }
    };
    const info = typeMap[type] || { label: type, color: 'bg-gray-50 text-gray-700' };
    return (
      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${info.color}`}>
        {info.label}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const priorityMap = {
      'low': { label: 'נמוך', color: 'text-gray-500' },
      'normal': { label: 'רגיל', color: 'text-blue-500' },
      'high': { label: 'גבוה', color: 'text-orange-500' },
      'urgent': { label: 'דחוף', color: 'text-red-500 font-bold' }
    };
    const info = priorityMap[priority] || { label: priority, color: 'text-gray-500' };
    return <span className={`text-xs ${info.color}`}>{info.label}</span>;
  };

  // Format helpers
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('he-IL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: 'ILS'
    }).format(amount || 0);
  };

  // View return details
  const handleViewDetails = async (returnItem) => {
    try {
      const response = await axios.get(`/api/admin/returns/${returnItem.id}`);
      setSelectedReturn(response.data.return);
      setShowDetailModal(true);
    } catch (error) {
      toast.error('שגיאה בטעינת פרטי ההחזרה');
    }
  };

  // Approve return
  const handleApprove = async () => {
    if (!selectedReturn) return;
    
    try {
      setActionLoading(true);
      await axios.put(`/api/admin/returns/${selectedReturn.id}/approve`, approveData);
      toast.success('ההחזרה אושרה בהצלחה');
      setShowApproveModal(false);
      setShowDetailModal(false);
      fetchReturns();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.message || 'שגיאה באישור ההחזרה');
    } finally {
      setActionLoading(false);
    }
  };

  // Reject return
  const handleReject = async () => {
    if (!selectedReturn || !rejectData.rejection_reason) {
      toast.error('יש לציין סיבת דחייה');
      return;
    }
    
    try {
      setActionLoading(true);
      await axios.put(`/api/admin/returns/${selectedReturn.id}/reject`, rejectData);
      toast.success('ההחזרה נדחתה');
      setShowRejectModal(false);
      setShowDetailModal(false);
      fetchReturns();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.message || 'שגיאה בדחיית ההחזרה');
    } finally {
      setActionLoading(false);
    }
  };

  // Update status
  const handleUpdateStatus = async (returnId, newStatus) => {
    try {
      await axios.put(`/api/admin/returns/${returnId}/status`, { status: newStatus });
      toast.success('הסטטוס עודכן');
      fetchReturns();
    } catch (error) {
      toast.error('שגיאה בעדכון הסטטוס');
    }
  };

  // Calculate total items value for a return
  const calculateItemsValue = (items) => {
    if (!items || !items.length) return 0;
    return items.reduce((sum, item) => {
      const price = item.unit_price || item.product?.price_b2b || 0;
      return sum + (price * (item.quantity || 1));
    }, 0);
  };

  return (
    <div className="p-6 space-y-6" dir="rtl">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">ניהול החזרות</h1>
          <p className="text-gray-600 text-sm mt-1">צפייה ואישור בקשות החזרה</p>
        </div>
        <button
          onClick={() => { fetchReturns(); fetchStats(); }}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          <ArrowPathIcon className="w-5 h-5" />
          רענון
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <ClockIcon className="w-5 h-5 text-yellow-600" />
              <span className="text-sm text-yellow-700">ממתינות לאישור</span>
            </div>
            <p className="text-2xl font-bold text-yellow-800 mt-2">{stats.pending_approval || 0}</p>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <CheckCircleIcon className="w-5 h-5 text-green-600" />
              <span className="text-sm text-green-700">אושרו</span>
            </div>
            <p className="text-2xl font-bold text-green-800 mt-2">
              {stats.by_status?.find(s => s.status === 'approved')?.count || 0}
            </p>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <TruckIcon className="w-5 h-5 text-blue-600" />
              <span className="text-sm text-blue-700">בדרך/התקבלו</span>
            </div>
            <p className="text-2xl font-bold text-blue-800 mt-2">
              {(stats.by_status?.find(s => s.status === 'in_transit')?.count || 0) + 
               (stats.by_status?.find(s => s.status === 'received')?.count || 0)}
            </p>
          </div>
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <CurrencyDollarIcon className="w-5 h-5 text-purple-600" />
              <span className="text-sm text-purple-700">סה"כ זיכויים</span>
            </div>
            <p className="text-2xl font-bold text-purple-800 mt-2">{formatCurrency(stats.total_refunded)}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border p-4">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="חיפוש לפי מספר RMA, חברה..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              className="w-full pr-10 pl-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          
          {/* Quick filters */}
          <select
            value={filters.status}
            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
            className="border rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500"
          >
            <option value="">כל הסטטוסים</option>
            <option value="requested">נשלח</option>
            <option value="pending_approval">ממתין לאישור</option>
            <option value="approved">אושר</option>
            <option value="rejected">נדחה</option>
            <option value="in_transit">בדרך</option>
            <option value="received">התקבל</option>
            <option value="completed">הושלם</option>
          </select>

          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50"
          >
            <FunnelIcon className="w-5 h-5" />
            סינון מתקדם
            <ChevronDownIcon className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {/* Advanced filters */}
        {showFilters && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 pt-4 border-t">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">סוג החזרה</label>
              <select
                value={filters.return_type}
                onChange={(e) => setFilters(prev => ({ ...prev, return_type: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2"
              >
                <option value="">הכל</option>
                <option value="defective">פגום</option>
                <option value="wrong_item">מוצר שגוי</option>
                <option value="damaged_in_shipping">נזק במשלוח</option>
                <option value="changed_mind">שינוי דעה</option>
                <option value="warranty_claim">אחריות</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">עדיפות</label>
              <select
                value={filters.priority}
                onChange={(e) => setFilters(prev => ({ ...prev, priority: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2"
              >
                <option value="">הכל</option>
                <option value="urgent">דחוף</option>
                <option value="high">גבוה</option>
                <option value="normal">רגיל</option>
                <option value="low">נמוך</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">מקור</label>
              <select
                value={filters.created_by_agent}
                onChange={(e) => setFilters(prev => ({ ...prev, created_by_agent: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2"
              >
                <option value="">הכל</option>
                <option value="true">סוכן שטח</option>
                <option value="false">לקוח</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">מתאריך</label>
              <input
                type="date"
                value={filters.date_from}
                onChange={(e) => setFilters(prev => ({ ...prev, date_from: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2"
              />
            </div>
          </div>
        )}
      </div>

      {/* Returns Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center p-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : returns.length === 0 ? (
          <div className="text-center p-12">
            <ExclamationTriangleIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900">אין החזרות</h3>
            <p className="text-gray-500 mt-1">לא נמצאו בקשות החזרה לפי הסינון שנבחר</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">מספר RMA</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">חברה</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">סוג</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">מוצרים</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">סטטוס</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">עדיפות</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">מקור</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">תאריך</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">פעולות</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {returns.map((returnItem) => (
                  <tr key={returnItem.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <span className="font-mono font-medium text-primary-600">{returnItem.rma_number}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <BuildingOfficeIcon className="w-4 h-4 text-gray-400" />
                        <div>
                          <p className="font-medium text-gray-900">{returnItem.company?.name || '-'}</p>
                          <p className="text-xs text-gray-500">{returnItem.order?.order_number}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {getReturnTypeBadge(returnItem.return_type)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm">
                        <span className="font-medium">
                          {returnItem.items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0}
                        </span>
                        <span className="text-gray-500 mr-1">פריטים</span>
                        <p className="text-xs text-gray-500">{formatCurrency(calculateItemsValue(returnItem.items))}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {getStatusBadge(returnItem.status)}
                    </td>
                    <td className="px-4 py-3">
                      {getPriorityBadge(returnItem.priority)}
                    </td>
                    <td className="px-4 py-3">
                      {returnItem.created_by_agent ? (
                        <div className="inline-flex items-center gap-1 text-xs text-indigo-600">
                          <UserGroupIcon className="w-4 h-4" />
                          <div>
                            <span className="font-medium">
                              {returnItem.user?.first_name && returnItem.user?.last_name 
                                ? `${returnItem.user.first_name} ${returnItem.user.last_name}`
                                : returnItem.agent?.name || 'סוכן'}
                            </span>
                            <p className="text-[10px] text-indigo-400">סוכן שטח</p>
                          </div>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-500">לקוח</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {formatDate(returnItem.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleViewDetails(returnItem)}
                          className="p-1 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded"
                          title="צפייה בפרטים"
                        >
                          <EyeIcon className="w-5 h-5" />
                        </button>
                        {['requested', 'pending_approval'].includes(returnItem.status) && (
                          <>
                            <button
                              onClick={() => { 
                                setSelectedReturn(returnItem); 
                                setApproveData(prev => ({
                                  ...prev,
                                  credit_amount: parseFloat(returnItem.total_refund_amount || 0)
                                }));
                                setShowApproveModal(true); 
                              }}
                              className="p-1 text-green-600 hover:bg-green-50 rounded"
                              title="אישור"
                            >
                              <CheckCircleIcon className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => { setSelectedReturn(returnItem); setShowRejectModal(true); }}
                              className="p-1 text-red-600 hover:bg-red-50 rounded"
                              title="דחייה"
                            >
                              <XCircleIcon className="w-5 h-5" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
            <div className="text-sm text-gray-500">
              מציג {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} מתוך {pagination.total}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page === 1}
                className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                הקודם
              </button>
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page >= pagination.pages}
                className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                הבא
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedReturn && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold">פרטי החזרה</h2>
                <p className="text-sm text-gray-500 font-mono">{selectedReturn.rma_number}</p>
              </div>
              <button
                onClick={() => setShowDetailModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Status & Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-gray-500">סטטוס</p>
                  <div className="mt-1">{getStatusBadge(selectedReturn.status)}</div>
                </div>
                <div>
                  <p className="text-sm text-gray-500">סוג החזרה</p>
                  <div className="mt-1">{getReturnTypeBadge(selectedReturn.return_type)}</div>
                </div>
                <div>
                  <p className="text-sm text-gray-500">עדיפות</p>
                  <div className="mt-1">{getPriorityBadge(selectedReturn.priority)}</div>
                </div>
                <div>
                  <p className="text-sm text-gray-500">תאריך יצירה</p>
                  <p className="mt-1 font-medium">{formatDate(selectedReturn.created_at)}</p>
                </div>
              </div>

              {/* Company & Order */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-medium mb-2 flex items-center gap-2">
                    <BuildingOfficeIcon className="w-5 h-5 text-gray-500" />
                    פרטי חברה
                  </h3>
                  <p className="font-medium">{selectedReturn.company?.name}</p>
                  <p className="text-sm text-gray-500">{selectedReturn.company?.email}</p>
                  <p className="text-sm text-gray-500">{selectedReturn.company?.phone}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-medium mb-2">
                    {selectedReturn.created_by_agent ? 'נוצר ע"י סוכן' : 'פרטי הזמנה'}
                  </h3>
                  {selectedReturn.created_by_agent ? (
                    <>
                      <p className="font-medium text-indigo-600">
                        {selectedReturn.user?.first_name && selectedReturn.user?.last_name 
                          ? `${selectedReturn.user.first_name} ${selectedReturn.user.last_name}`
                          : 'סוכן'}
                      </p>
                      <p className="text-sm text-gray-500">{selectedReturn.user?.email}</p>
                      <p className="text-sm text-gray-500">{selectedReturn.user?.phone}</p>
                    </>
                  ) : (
                    <>
                      <p className="font-mono">{selectedReturn.order?.order_number}</p>
                      <p className="text-sm text-gray-500">סה"כ: {formatCurrency(selectedReturn.order?.total_amount)}</p>
                      <p className="text-sm text-gray-500">{formatDate(selectedReturn.order?.created_at)}</p>
                    </>
                  )}
                </div>
              </div>

              {/* Items */}
              <div>
                <h3 className="font-medium mb-3">פריטים להחזרה</h3>
                <div className="border rounded-lg overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">מוצר</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">כמות</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">סיבה</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">מחיר</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {selectedReturn.items?.map((item) => (
                        <tr key={item.id}>
                          <td className="px-4 py-2">
                            <div className="flex items-center gap-2">
                              {item.product?.images?.[0] && (
                                <img src={item.product.images[0]} alt="" className="w-10 h-10 object-cover rounded" />
                              )}
                              <div>
                                <p className="font-medium text-sm">{item.product?.name}</p>
                                <p className="text-xs text-gray-500">{item.product?.sku}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-2 text-sm">{item.quantity}</td>
                          <td className="px-4 py-2 text-sm text-gray-500">{item.reason || '-'}</td>
                          <td className="px-4 py-2 text-sm">{formatCurrency(item.unit_price || item.product?.price_b2b)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Customer Notes */}
              {selectedReturn.customer_notes && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h3 className="font-medium text-yellow-800 mb-2">הערות הלקוח</h3>
                  <p className="text-sm text-yellow-900">{selectedReturn.customer_notes}</p>
                </div>
              )}

              {/* Reason */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-medium mb-2">סיבת ההחזרה</h3>
                <p className="text-gray-700">{selectedReturn.return_reason}</p>
              </div>

              {/* Photos */}
              {selectedReturn.photos?.length > 0 && (
                <div>
                  <h3 className="font-medium mb-3">תמונות</h3>
                  <div className="flex gap-2 flex-wrap">
                    {selectedReturn.photos.map((photo) => (
                      <img
                        key={photo.id}
                        src={photo.photo_url}
                        alt={photo.description || 'תמונת החזרה'}
                        className="w-24 h-24 object-cover rounded-lg border cursor-pointer hover:opacity-75"
                        onClick={() => window.open(photo.photo_url, '_blank')}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              {['requested', 'pending_approval'].includes(selectedReturn.status) && (
                <div className="flex gap-3 pt-4 border-t">
                  <button
                    onClick={() => { 
                      setApproveData(prev => ({
                        ...prev,
                        credit_amount: parseFloat(selectedReturn.total_refund_amount || 0)
                      }));
                      setShowDetailModal(false); 
                      setShowApproveModal(true); 
                    }}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    <CheckCircleIcon className="w-5 h-5" />
                    אשר החזרה
                  </button>
                  <button
                    onClick={() => { setShowDetailModal(false); setShowRejectModal(true); }}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700"
                  >
                    <XCircleIcon className="w-5 h-5" />
                    דחה החזרה
                  </button>
                </div>
              )}

              {/* Status update for approved returns */}
              {selectedReturn.status === 'approved' && (
                <div className="flex gap-3 pt-4 border-t">
                  <button
                    onClick={() => handleUpdateStatus(selectedReturn.id, 'in_transit')}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    סמן כבדרך
                  </button>
                  <button
                    onClick={() => handleUpdateStatus(selectedReturn.id, 'received')}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                  >
                    סמן כהתקבל
                  </button>
                </div>
              )}

              {selectedReturn.status === 'received' && (
                <div className="flex gap-3 pt-4 border-t">
                  <button
                    onClick={() => handleUpdateStatus(selectedReturn.id, 'inspecting')}
                    className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
                  >
                    סמן כבבדיקה
                  </button>
                  <button
                    onClick={() => handleUpdateStatus(selectedReturn.id, 'completed')}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    סמן כהושלם
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Approve Modal */}
      {showApproveModal && selectedReturn && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="border-b px-6 py-4">
              <h2 className="text-xl font-bold text-green-600">אישור החזרה</h2>
              <p className="text-sm text-gray-500">{selectedReturn.rma_number}</p>
            </div>
            <div className="p-6 space-y-4">
              {/* Company & Reason Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-sm text-gray-600 mb-2">
                  <strong>חברה:</strong> {selectedReturn.company?.name}
                </div>
                <div className="text-sm text-gray-600 mb-2">
                  <strong>סיבת החזרה:</strong> {selectedReturn.return_reason}
                </div>
                <div className="text-sm text-gray-600">
                  <strong>סוג:</strong> {selectedReturn.return_type}
                </div>
              </div>

              {/* Items List */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">פריטים להחזרה:</label>
                <div className="border rounded-lg divide-y max-h-48 overflow-y-auto">
                  {selectedReturn.items?.map((item, idx) => (
                    <div key={item.id || idx} className="p-3 flex items-center gap-3">
                      <img 
                        src={item.product?.images?.[0] || '/placeholder-product.png'} 
                        alt="" 
                        className="w-12 h-12 object-cover rounded"
                      />
                      <div className="flex-1">
                        <div className="font-medium text-sm">{item.product?.name}</div>
                        <div className="text-xs text-gray-500">
                          כמות: {item.quantity} | מחיר: ₪{parseFloat(item.unit_price || 0).toFixed(2)}
                        </div>
                      </div>
                      <div className="text-green-600 font-medium">
                        ₪{parseFloat(item.refund_amount || 0).toFixed(2)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Credit Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  סכום זיכוי (₪)
                </label>
                <div className="text-xs text-gray-500 mb-2">
                  סכום מבוקש: ₪{parseFloat(selectedReturn.total_refund_amount || 0).toFixed(2)}
                </div>
                <input
                  type="number"
                  value={approveData.credit_amount}
                  onChange={(e) => setApproveData(prev => ({ ...prev, credit_amount: parseFloat(e.target.value) || 0 }))}
                  className="w-full border rounded-lg px-3 py-2 text-lg font-bold"
                  min="0"
                  step="0.01"
                />
              </div>

              {/* Return to Inventory */}
              <div className="bg-blue-50 rounded-lg p-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={approveData.return_to_inventory}
                    onChange={(e) => setApproveData(prev => ({ ...prev, return_to_inventory: e.target.checked }))}
                    className="w-5 h-5 rounded text-primary-600"
                  />
                  <div>
                    <div className="font-medium">החזר למלאי</div>
                    <div className="text-sm text-gray-600">סמן אם המוצרים במצב תקין ויש להחזירם למלאי</div>
                  </div>
                </label>
              </div>

              {/* Internal Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">הערות פנימיות</label>
                <textarea
                  value={approveData.internal_notes}
                  onChange={(e) => setApproveData(prev => ({ ...prev, internal_notes: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2"
                  rows={2}
                  placeholder="הערות לצוות הפנימי..."
                />
              </div>
            </div>
            <div className="border-t px-6 py-4 flex gap-3 sticky bottom-0 bg-white">
              <button
                onClick={handleApprove}
                disabled={actionLoading}
                className="flex-1 bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium"
              >
                {actionLoading ? 'מאשר...' : `אשר וזכה ₪${approveData.credit_amount.toFixed(2)}`}
              </button>
              <button
                onClick={() => setShowApproveModal(false)}
                className="flex-1 border py-3 rounded-lg hover:bg-gray-50"
              >
                ביטול
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && selectedReturn && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="border-b px-6 py-4">
              <h2 className="text-xl font-bold text-red-600">דחיית החזרה</h2>
              <p className="text-sm text-gray-500">{selectedReturn.rma_number}</p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">סיבת דחייה *</label>
                <textarea
                  value={rejectData.rejection_reason}
                  onChange={(e) => setRejectData(prev => ({ ...prev, rejection_reason: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2"
                  rows={3}
                  placeholder="נא לציין את סיבת הדחייה..."
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">הערות פנימיות</label>
                <textarea
                  value={rejectData.internal_notes}
                  onChange={(e) => setRejectData(prev => ({ ...prev, internal_notes: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2"
                  rows={2}
                />
              </div>
            </div>
            <div className="border-t px-6 py-4 flex gap-3">
              <button
                onClick={handleReject}
                disabled={actionLoading || !rejectData.rejection_reason}
                className="flex-1 bg-red-600 text-white py-2 rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {actionLoading ? 'דוחה...' : 'דחה החזרה'}
              </button>
              <button
                onClick={() => setShowRejectModal(false)}
                className="flex-1 border py-2 rounded-lg hover:bg-gray-50"
              >
                ביטול
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminReturnsPage;
